import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../AuthContext';
import { db } from '../firebase';
import { doc, onSnapshot, collection } from 'firebase/firestore';
import { PERMISSIONS, getUserRoles, hasPermission } from '../utils/rbac';
import Header from '../components/Header';
import NavigationBar from '../components/NavigationBar';
import QuoteRandomizer from '../components/QuoteRandomizer';
import AIChat from '../components/AIChat';
import ProPoem from '../components/ProPoem';
import YouTubeFeature from '../components/YouTubeFeature';
import InstagramFeature from '../components/InstagramFeature';
import TikTokFeature from '../components/TikTokFeature';
import TwitterFeature from '../components/TwitterFeature';
import Gallery from '../components/Gallery';
import BaseGallery from '../components/galleries/BaseGallery';
import AdminDashboard from '../components/AdminDashboard';
import ProUserChat from '../components/ProUserChat';
import ProGallerySelector from '../components/ProGallerySelector';
import MissionStatement from '../components/MissionStatement';
import CustomBuild from '../components/CustomBuild';
import MyLinkTree from '../components/MyLinkTree';
import Videos from '../components/Videos';
import { Link, useLocation } from 'react-router-dom';
import Tech from '../components/Tech';
import WhatIsNew from '../components/WhatIsNew';
import PurchasedContent from '../components/personalstuff/PurchasedContent';
import TrackPurchases from '../components/personalstuff/TrackPurchases';
import AnnouncementModal from '../components/AnnouncementModal';
import {
    ANNOUNCEMENT_VERSION,
    ANNOUNCEMENT_TITLE,
    ANNOUNCEMENT_MESSAGE,
    ANNOUNCEMENT_SIGNATURE,
} from '../content/shutdownAnnouncement';
import './AuthedHomePage.css';

const ANNOUNCEMENT_SESSION_KEY = `dannytopia_announcement_dismissed_${ANNOUNCEMENT_VERSION}`;

const AuthedHomePage = () => {
    const { user, isAdmin } = useContext(AuthContext);
    const [isPro, setIsPro] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [userRoles, setUserRoles] = useState([]);
    const location = useLocation();
    const [activeComponent, setActiveComponent] = useState('whats_new');
    const [dynamicGalleries, setDynamicGalleries] = useState([]);
    const [showAnnouncement, setShowAnnouncement] = useState(false);

    useEffect(() => {
        if (sessionStorage.getItem(ANNOUNCEMENT_SESSION_KEY)) return;
        setShowAnnouncement(true);
    }, []);

    const dismissAnnouncement = () => {
        sessionStorage.setItem(ANNOUNCEMENT_SESSION_KEY, 'true');
        setShowAnnouncement(false);
    };

    useEffect(() => {
        if (!user?.uid) return;

        const unsubscribe = onSnapshot(
            doc(db, 'users', user.uid),
            (doc) => {
                if (doc.exists()) {
                    const userData = doc.data();
                    
                    // Check for new role-based system first, then fallback to legacy boolean
                    const isProUser = userData.role === 'pro_user' || userData.isPro === true;
                    setIsPro(isProUser);
                    setProfileImage(userData.userProfile?.profileImage);
                    // Set user roles based on current data
                    setUserRoles(getUserRoles({ ...userData, isAdmin }));
                }
            }
        );

        return () => unsubscribe();
    }, [user, isAdmin]);

    useEffect(() => {
        const galleriesRef = collection(db, 'gallery_config');
        const unsubscribe = onSnapshot(galleriesRef, (snapshot) => {
            const galleries = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setDynamicGalleries(galleries);
        });

        return () => unsubscribe();
    }, []);

    // Debug logging removed for production

    useEffect(() => {
        if (location.state?.targetComponent) {
            setActiveComponent(location.state.targetComponent);
            if (location.state.targetTab) {
                sessionStorage.setItem('customBuildTab', location.state.targetTab);
            }
        }
    }, [location]);

    const renderActiveComponent = () => {
        const dynamicGallery = dynamicGalleries.find(
            gallery => gallery.collectionName === activeComponent
        );
        
        if (dynamicGallery) {
            return hasPermission(userRoles, PERMISSIONS.VIEW_PRO_CONTENT) &&
                   <BaseGallery
                       collectionName={dynamicGallery.collectionName}
                       onBack={() => setActiveComponent('pro_galleries')}
                   />;
        }

        // Handle tech sections
        if (activeComponent.startsWith('tech_')) {
            const section = activeComponent.replace('tech_', '');
            return <Tech defaultSection={section} />;
        }

        switch (activeComponent) {
            case 'youtube':
                return <YouTubeFeature />;
            case 'instagram':
                return <InstagramFeature />;
            case 'tiktok':
                return <TikTokFeature />;
            case 'twitter':
                return hasPermission(userRoles, PERMISSIONS.USE_TWITTER_FEATURE) && <TwitterFeature />;
            case 'poem':
                return hasPermission(userRoles, PERMISSIONS.USE_PRO_POEM) && <ProPoem />;
            case 'gallery':
                return <Gallery />;
            case 'pro_chat':
                return hasPermission(userRoles, PERMISSIONS.USE_PRO_CHAT) && <ProUserChat />;
            case 'admin':
                return hasPermission(userRoles, PERMISSIONS.VIEW_ADMIN_DASHBOARD) && <AdminDashboard />;
            case 'pro_galleries':
                return hasPermission(userRoles, PERMISSIONS.VIEW_PRO_CONTENT) && 
                       <ProGallerySelector setActiveComponent={setActiveComponent} />;
            case 'custom_build':
                return hasPermission(userRoles, PERMISSIONS.USE_CUSTOM_BUILD) && <CustomBuild />;
            case 'my_linktree':
                return <MyLinkTree />;
            case 'whats_new':
                return <WhatIsNew isPro={isPro} isAdmin={isAdmin} setActiveComponent={setActiveComponent} />;
            case 'tech':
                return <Tech />;
            case 'videos':
                return <Videos />;
            case 'track_purchases':
                return hasPermission(userRoles, PERMISSIONS.TRACK_PURCHASES) && <TrackPurchases />;
            case 'purchased_content':
                return hasPermission(userRoles, PERMISSIONS.VIEW_PURCHASED_CONTENT) && <PurchasedContent />;
            default:
                return <WhatIsNew isPro={isPro} isAdmin={isAdmin} setActiveComponent={setActiveComponent} />;
        }
    };

    return (
        <div className="authed-homepage-container">
            <Header user={user} profileImage={profileImage} />
            <NavigationBar 
                isPro={isPro} 
                activeComponent={activeComponent} 
                setActiveComponent={setActiveComponent}
                userRoles={userRoles}
            />
            
            <div className={`content-container${activeComponent === 'admin' ? ' content-container--admin' : ''}`}>
                {renderActiveComponent()}
            </div>
            
            <footer className="auth-footer">
                <Link to="/privacy-policy" className="footer-link">Privacy Policy</Link>
                <Link to="/contact" className="footer-link">Contact</Link>
                <span className="footer-copyright">© 2026 Moon Park LLC</span>
            </footer>

            {showAnnouncement && (
                <AnnouncementModal
                    title={ANNOUNCEMENT_TITLE}
                    message={ANNOUNCEMENT_MESSAGE}
                    signature={ANNOUNCEMENT_SIGNATURE}
                    onClose={dismissAnnouncement}
                />
            )}
        </div>
    );
};

export default AuthedHomePage;